/**
 * The one rule for every flooring-type picker, on every platform. A picker
 * never invents a type the host's list lacks: an unset type shows a
 * placeholder, a picked type offers an entry that unpicks it, and an empty
 * list with nothing picked leaves nothing to pick. The platforms draw the
 * rows and wire the picks; everything they show comes from here.
 */

import { DEFAULT_FLOOR_TYPE } from '@kaiizen/kai-floor-plan';

import type { FloorTypeOption } from './editorConstants';

/** What a flooring picker shows for one value. */
export interface FloorTypeChoices {
  /** The rows in display order: `key` tells them apart and is what a pick hands back, `label` is what shows. */
  items: { key: string; label: string }[];
  /** The row showing as picked; undefined when nothing is picked. */
  selectedKey: string | undefined;
  /** What the picker shows while nothing is picked. */
  placeholder: string;
  /** False when there is nothing to pick: no allowed types and nothing picked. */
  enabled: boolean;
}

const UNPICK_KEY = '__unset__';

/** What a region with no floor type reads as: the pick list's clearing entry, and a read-only panel's text. */
const NO_FLOOR_TYPE_LABEL = 'No flooring type';

const UNPICK_ITEM = { key: UNPICK_KEY, label: NO_FLOOR_TYPE_LABEL };

/**
 * The rows for a picked `value`: the unpick entry when something is picked,
 * the value verbatim when the allowed list lacks it (a plan authored under a
 * different list still shows the truth), then the allowed options.
 */
const choiceItems = (options: FloorTypeOption[], value: string | undefined): FloorTypeChoices['items'] => [
  ...(value ? [UNPICK_ITEM] : []),
  ...(value && !options.some(option => option.key === value) ? [{ key: value, label: value }] : []),
  ...options.map(({ key, label }) => ({ key, label })),
];

/**
 * A flooring picker's rows and state for `value` over the editor's allowed
 * `options`. An empty or missing `value` is unset (an element stores unset
 * as `''`).
 */
export const floorTypeChoices = (options: FloorTypeOption[], value: string | undefined): FloorTypeChoices => {
  const enabled = options.length > 0 || !!value;
  return {
    items: choiceItems(options, value),
    selectedKey: value || undefined,
    placeholder: enabled ? 'Select flooring type' : 'No flooring types available',
    enabled,
  };
};

/** The floor type a picked row's key stands for: the unpick entry's is undefined. */
export const pickedFloorType = (key: string): string | undefined => key === UNPICK_KEY ? undefined : key;

/**
 * The floor type a flooring-by-type row shows as picked: the scene names its
 * unset bucket `DEFAULT_FLOOR_TYPE`, a pseudo-type that shows as unset.
 */
export const floorTypeOfSceneType = (sceneType: string): string | undefined => sceneType === DEFAULT_FLOOR_TYPE ? undefined : sceneType;

/** What a floor type reads as where it is shown rather than picked (a read-only panel): its option's label, the value as stored when the allowed list lacks it, or "No flooring type" when unset. */
export function floorTypeLabel(options: FloorTypeOption[], value: string | undefined): string {
  const { items, selectedKey } = floorTypeChoices(options, value);
  return items.find(item => item.key === selectedKey)?.label ?? NO_FLOOR_TYPE_LABEL;
}
